import {
  QButton,
  QCheckbox,
  QCloseButton,
  deprecated,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QText,
  useToastProvider,
} from '@qualio/ui-components';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import draftUpgradeApi from '../../../services/draftUpgradeApi';
import { UpgradableDraft, UpgradeDraftResponse, UpgradeRequest } from '../../../types';

type UpdateDraftsModalProps = {
  drafts: UpgradableDraft[];
  companyId: number;
  templateId: number;
  version: number | string;
  isOpen: boolean;
  onClose: any;
  onSubmit: any;
};

const UpdateDraftsModal: React.FC<UpdateDraftsModalProps> = (props) => {
  const { t } = useTranslation();
  const intial_array: number[] = [];
  const [checkedDraftIds, setCheckedDraftIds] = useState(intial_array);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const { showToast } = useToastProvider();
  const [draftUpgradeApiLoading, setDraftUpgradeApiLoading] = useState(false);

  const allChecked = checkedDraftIds.length === props.drafts.length;

  const toggleDraft = (toggledId: number, checked: boolean) => {
    if (checked) {
      setCheckedDraftIds((prevChecked) => prevChecked.concat(toggledId));
      setSubmitEnabled(true);
    } else {
      if (checkedDraftIds.length === 1) {
        setSubmitEnabled(false);
      }
      setCheckedDraftIds((prevChecked) => prevChecked.filter((draftId) => draftId !== toggledId));
    }
  };

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setCheckedDraftIds(props.drafts.map((d) => d.id));
      setSubmitEnabled(true);
    } else {
      setCheckedDraftIds([]);
      setSubmitEnabled(false);
    }
  };

  const formRow = (draft: UpgradableDraft) => {
    return {
      check: (
        <QCheckbox
          key={draft.id}
          isChecked={checkedDraftIds.includes(draft.id)}
          onChange={(e) => toggleDraft(draft.id, e.target.checked)}
        />
      ),
      id: draft.code,
      title: draft.title,
      templateVersion: draft.major_version + '.0',
    };
  };

  const submitDraftUpgrade = () => {
    const request: UpgradeRequest = {
      target_template_id: props.templateId,
      documents_ids: checkedDraftIds,
      company_id: props.companyId,
    };
    setSubmitEnabled(false);
    setDraftUpgradeApiLoading(true);
    draftUpgradeApi
      .upgradeDrafts(request)
      .then((value: UpgradeDraftResponse) => {
        console.log(`document ids updated: ${value.updated_documents_ids}`);
        showToast({
          title: t('common.toast.success'),
          description: t('template.updateDrafts.modal.success'),
          status: 'success',
          'data-cy': 'toast-update-drafts-success',
        });
        setDraftUpgradeApiLoading(false);
        props.onSubmit();
      })
      .catch((error) => {
        console.error(error);
        showToast({
          title: t('common.toast.error'),
          description: determineToastErrorMessage(error),
          status: 'error',
          'data-cy': 'toast-update-drafts-error',
        });
        setDraftUpgradeApiLoading(false);
        setSubmitEnabled(true);
      });
  };

  const determineToastErrorMessage = (error: any): string => {
    // extract informational MTBE message and use it for toast message, if it is of certain format
    const errMsg = parseMessageFromMTBEError(error);
    if (errMsg && errMsg.includes('currently being edited')) {
      return errMsg;
    }
    // default message if unknown error format encountered
    return t('template.updateDrafts.modal.error');
  };

  const parseMessageFromMTBEError = (error: any): string | null => {
    const msg = error?.response?.data?.message;
    if (msg) {
      return msg as string;
    }
    return null;
  };

  return (
    <QModal isOpen={props.isOpen} onClose={props.onClose} size="4xl">
      <QModalHeader>
        <QText>
          {t('template.updateDrafts.modal.title')} {props.version}
        </QText>
        <QCloseButton onClick={props.onClose} />
      </QModalHeader>
      <QModalBody>
        <deprecated.QDataTable
          columns={[
            {
              Header: <QCheckbox isChecked={allChecked} onChange={(e) => toggleAll(e.target.checked)} />,
              accessor: 'check',
              disableSortBy: true,
              width: '10%',
            },
            {
              Header: t('template.updateDrafts.modal.table.id'),
              accessor: 'id',
              width: '10%',
            },
            {
              Header: t('template.updateDrafts.modal.table.title'),
              accessor: 'title',
              width: '60%',
            },
            {
              Header: t('template.updateDrafts.modal.currentVersion'),
              accessor: 'templateVersion',
              width: '20%',
            },
          ]}
          data={props.drafts.map(formRow)}
        />
      </QModalBody>
      <QModalActions>
        <QButton onClick={props.onClose} variant="outline" isDisabled={draftUpgradeApiLoading}>
          {t('common.buttons.cancel')}
        </QButton>
        <QButton
          isDisabled={!submitEnabled || draftUpgradeApiLoading}
          isLoading={draftUpgradeApiLoading}
          onClick={submitDraftUpgrade}
        >
          {t('template.updateDrafts.modal.upgradeButton')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};

export default UpdateDraftsModal;
